import { useState } from 'react';
import { Button } from '@shared/ui/Button';
import { Actions, Muted } from '../kfo/ui';
import { chartFromPaste, parseDecimal, type ChartPointDraft } from './model';
import { CellInput, ChartTable, ChartWrap, EditArea } from './ui';

/**
 * Редактор ГВХ — пары «вылет, м → г/п, т». Точки вводятся по одной или
 * вставляются столбцами из Excel/паспорта крана («Вставить списком»).
 */
export function LoadChartEditor({
  points,
  onChange,
}: {
  points: ChartPointDraft[];
  onChange: (points: ChartPointDraft[]) => void;
}) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');

  const set = (i: number, patch: Partial<ChartPointDraft>) =>
    onChange(points.map((p, k) => (k === i ? { ...p, ...patch } : p)));

  function applyPaste() {
    const parsed = chartFromPaste(pasteText);
    if (parsed.length > 0) {
      onChange([...points.filter((p) => p.reach.trim() || p.cap.trim()), ...parsed]);
    }
    setPasteText('');
    setPasteOpen(false);
  }

  return (
    <ChartWrap data-ppr="load-chart">
      {points.length === 0 ? (
        <Muted>ГВХ не задана — проверка крана пойдёт по максимальной грузоподъёмности.</Muted>
      ) : (
        <ChartTable>
          <thead>
            <tr>
              <th>Вылет, м</th>
              <th>Г/п, т</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {points.map((p, i) => (
              <tr key={i}>
                <td>
                  <CellInput
                    value={p.reach}
                    inputMode="decimal"
                    aria-label={`Вылет, точка ${i + 1}`}
                    $invalid={Number.isNaN(parseDecimal(p.reach))}
                    onChange={(e) => set(i, { reach: e.target.value })}
                  />
                </td>
                <td>
                  <CellInput
                    value={p.cap}
                    inputMode="decimal"
                    aria-label={`Г/п, точка ${i + 1}`}
                    $invalid={Number.isNaN(parseDecimal(p.cap))}
                    onChange={(e) => set(i, { cap: e.target.value })}
                  />
                </td>
                <td>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    title="Удалить точку"
                    onClick={() => onChange(points.filter((_, k) => k !== i))}
                  >
                    ✕
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </ChartTable>
      )}
      <Actions>
        <Button
          $variant="secondary"
          $size="sm"
          type="button"
          onClick={() => onChange([...points, { reach: '', cap: '' }])}
        >
          + Точка
        </Button>
        <Button $variant="ghost" $size="sm" type="button" onClick={() => setPasteOpen((v) => !v)}>
          Вставить списком
        </Button>
      </Actions>
      {pasteOpen && (
        <div>
          <EditArea
            $rows={4}
            value={pasteText}
            placeholder={'Вылет и г/п построчно, например:\n3 25\n4,5 18,2\n6 13'}
            onChange={(e) => setPasteText(e.target.value)}
          />
          <Actions>
            <Button $variant="primary" $size="sm" type="button" onClick={applyPaste}>
              Добавить точки
            </Button>
          </Actions>
        </div>
      )}
    </ChartWrap>
  );
}
